'use client';
import {
  InputForm,
  InputPassword,
  Text,
  Title,
  CustomLink,
  Button,
  OauthButton,
} from '@ui/components';
import { IAuthErrorCallback, ISignUpForm, useSignUpState } from './sign-up.state';
import { LEGAL_LINKS } from '@ui/constants';
import { sendGAEvent } from '@next/third-parties/google';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
interface ISignUpV2 {
  title: string;
  type: 'creator' | 'fan';
  onSignUp(form: ISignUpForm, errorCb: IAuthErrorCallback): void;
  oAuth?: boolean;
  fullName?: string;
  email?: string;
  isInAppBrowser?: boolean;
}

export function SignUpV2Component({
  title,
  type,
  onSignUp,
  oAuth = false,
  fullName,
  email,
  isInAppBrowser = false,
}: ISignUpV2) {
  const router = useRouter();
  const {
    isLoading,
    nameInput,
    emailInput,
    phoneInput,
    displayNameInput,
    passwordInput,
    checkBoxInput,
    isFormValid,
    isError,
    onSubmit,
  } = useSignUpState(onSignUp, type, { name: fullName, email });

  const searchParams = useSearchParams();
  const badgeId = searchParams.get('badgeId');
  const userId = searchParams.get('userId');
  const claimCode = searchParams.get('claimCode');
  const productId = searchParams.get('productId');
  const creatorId = searchParams.get('creatorId');
  const urlRedirect = searchParams.get('url-redirect');

  const ID_OF_BADGE_TO_CLAIM = process.env['NEXT_PUBLIC_BADGE_CLAIM_ID'] as string;

  let signinHref = '/auth/login';
  const params = new URLSearchParams();

  if (badgeId) {
    params.set('badgeId', badgeId);
  }

  if (userId) {
    params.set('userId', userId);
  }

  if (claimCode) {
    params.set('claimCode', claimCode);
  }

  if (productId) {
    params.set('productId', productId);
  }

  if (creatorId) {
    params.set('creatorId', creatorId);
  }

  if (urlRedirect) {
    params.set('url-redirect', urlRedirect);
  }

  signinHref = `/auth/login?${params.toString()}`;

  useEffect(() => {
    if (!badgeId && ID_OF_BADGE_TO_CLAIM) {
      router.replace(`?badgeId=${ID_OF_BADGE_TO_CLAIM}`);
    }
  }, [badgeId, ID_OF_BADGE_TO_CLAIM]);

  const handleSignUpClick = () => {
    if (type === 'fan') {
      sendGAEvent('event', 'fanSignupClicked', {
        signup_time: new Date().toISOString(),
      });
    } else if (type === 'creator') {
      sendGAEvent('event', 'creatorSignupClicked', {
        signup_time: 'Creator',
      });
    }
    onSubmit();
  };

  return (
    <div className="flex h-full w-full flex-col justify-center items-center space-y-4">
      <Title type="l" className="text-center lg:text-left">
        {title}
      </Title>

      {isInAppBrowser && (
        <>
          {oAuth && (
            <OauthButton type="google" url="/auth/google" badgeId={badgeId} claimCode={claimCode} />
          )}
          {oAuth && (
            <div className="my-6 flex w-full items-center">
              <div className="flex-1 border-t border-gray-200" />
              <span className="mx-4 text-gray-400">or</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>
          )}
        </>
      )}
      <div className="mb-4 w-full">
        <InputForm disabled={isLoading} label="Full Name" placeholder="Name" form={nameInput} />
      </div>
      {type === 'creator' && (
        <div className="mb-4 w-full">
          <InputForm
            disabled={isLoading}
            label="Display Name"
            placeholder="Display Name"
            form={displayNameInput}
          />
        </div>
      )}
      <div className="mb-4 w-full">
        <InputForm disabled={isLoading} label="Email" placeholder="Email" form={emailInput} />
      </div>
      {type === 'fan' && (
        <div className="mb-4 w-full">
          <InputForm
            disabled={isLoading}
            label="Phone (optional)"
            placeholder="+123-450-671"
            form={phoneInput}
          />
        </div>
      )}
      <div className="w-full">
        <InputPassword
          disabled={isLoading}
          label="Password"
          placeholder="Password"
          form={passwordInput}
        />
      </div>
      <div className="mb-12 mt-4 flex justify-center sm:mb-10 w-full ">
        <InputForm disabled={isLoading} form={checkBoxInput} type="checkbox" />
        <Text type="s">
          I have read and agreed to KOR Crew&apos;s{' '}
          <CustomLink type="s" href={LEGAL_LINKS.terms} target="_blank">
            Terms of Service
          </CustomLink>
          {' and '}
          <CustomLink type="s" href={LEGAL_LINKS.privacy} target="_blank">
            Privacy Policy
          </CustomLink>
        </Text>
      </div>
      <Button
        isLoading={isLoading}
        disabled={!isFormValid || isError}
        type="primary"
        onClick={handleSignUpClick}
        className="mb-4 w-full">
        {productId ? 'Create Account' : 'Collect Badge'}
      </Button>
      <Text type="m" className="text-center">
        <span>Already have an account? </span>
        <Link href={signinHref} className="cursor-pointer text-[#FF6600] font-semibold">
          Sign in
        </Link>
      </Text>
    </div>
  );
}
